import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { TOKEN, LINKS } from "@/lib/site";
import { SocialLinks } from "./SocialLinks";

const AddressRow = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="w-full group flex items-center justify-between gap-3 font-mono text-xs bg-black/40 border border-white/10 hover:border-[#ff007f] rounded-lg px-4 py-3 transition-colors"
    >
      <span className="text-left">
        <span className="block text-white/40 uppercase tracking-[0.2em] text-[10px] mb-1">{label}</span>
        <span className="block truncate text-white/80">{value}</span>
      </span>
      {copied ? <Check className="w-4 h-4 text-[#39ff14] shrink-0" /> : <Copy className="w-4 h-4 text-white/40 group-hover:text-[#ff007f] shrink-0" />}
    </button>
  );
};

export const Footer = () => {
  return (
    <footer className="relative pt-24 pb-10 px-5 sm:px-6 border-t border-white/10 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-street uppercase tracking-tight leading-[0.9] text-4xl sm:text-6xl lg:text-7xl mb-10"
        >
          Join the <span className="hero-gradient-text">HRS Army</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
          <div className="flex flex-wrap gap-3">
            <a href={LINKS.pumpfun} target="_blank" rel="noopener noreferrer" data-testid="footer-buy-btn"
              className="font-display font-bold text-sm px-7 py-3.5 rounded-full bg-[#ff007f] text-black hover:bg-[#ffb800] transition-colors glow-magenta">
              BUY ON PUMP.FUN
            </a>
            <a href={LINKS.dexscreener} target="_blank" rel="noopener noreferrer" data-testid="footer-dex-btn"
              className="font-display font-bold text-sm px-7 py-3.5 rounded-full border border-[#00f0ff]/50 text-[#00f0ff] hover:bg-[#00f0ff] hover:text-black transition-colors inline-flex items-center gap-2">
              DEXSCREENER <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="grid gap-3">
            <AddressRow label="Token Contract" value={TOKEN.contract} testid="footer-copy-contract" />
            <AddressRow label="Treasury / Info" value={TOKEN.treasury} testid="footer-copy-treasury" />
          </div>
        </div>

        {/* Socials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-3">Follow the Flock</div>
          <SocialLinks />
        </motion.div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 font-mono text-xs text-white/40">
          <span>© {new Date().getFullYear()} Homelessness Reform Society · $AHHH on {TOKEN.chain}</span>
          <span className="max-w-md">
            Not financial advice. Crypto is volatile and high-risk. $AHHH is a meme token for a
            philanthropic mission — participate responsibly.
          </span>
        </div>
      </div>

      <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-[#ff007f]/15 blur-3xl pointer-events-none" />
    </footer>
  );
};
